import { useCallback, useMemo, useState } from 'react';
import type { FC, MouseEventHandler, ReactElement } from 'react';
import { Box, Flex, Text, Tooltip, useBreakpointValue } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import { ShippingOptionType } from '../../bff-api/baskets/model';
import { BFFHttpError } from '../../bff-api/model/BFFHttpError';
import { UIErrorCode, errorMessage } from '../../bff-api/utils/error-handling-utils';
import { RecommendationLocation } from '../../components/recommendations/RecommendationLocation';
import { shouldOpenAddToBagDrawer } from '../../components/recommendations/utils/recommendations-utils';
import { useSLTToast } from '../../hooks/use-toast/useSLTToast';
import { useAddProductToBag } from '../../hooks/useAddProductToBag';
import { EventAction, EventLocation, useBloomreach } from '../../hooks/useBloomreach';
import { IconButton } from '../button/icon-button';
import { RegularPrice } from './price/RegularPrice';
import { SalePrice } from './price/SalePrice';
import { SuggestedPrice } from './price/SuggestedPrice';
import type { Product } from '../../bff-api/products/model';
import type { GTMSourceEvent } from '../../hooks/gtm/model/events/GTMSourceEvent';

export interface ProductPriceProps {
    location: RecommendationLocation;
    product: Product;
    hideSuggLabel?: boolean;
    widgetId?: string;
    widgetType?: string;
    metadataType?: string;
    onAddProductToBag?: (product: Product) => void;
}

export const ProductPrice: FC<ProductPriceProps> = ({
    product,
    location,
    widgetId,
    widgetType,
    metadataType,
    hideSuggLabel = false,
    onAddProductToBag,
}) => {
    const isDrawer = location === RecommendationLocation.DRAWER;
    const openDrawer = shouldOpenAddToBagDrawer(location);

    const { price = 0, prices = {}, c_canonicalProductId } = product;
    const { suggestedPrice, regularPrice } = prices;
    const { widgetClicked } = useBloomreach();
    const addProductToBag = useAddProductToBag({
        ignoreInventory: true,
        openDrawer,
        concatAddedProducts: isDrawer,
    });
    const isPriceHeader = useBreakpointValue({ base: false, sm: true });
    const toast = useSLTToast();
    const intl = useIntl();
    const [isLimitReached, setIsLimitReached] = useState(false);

    const onAddToBag: MouseEventHandler<HTMLButtonElement> = useCallback(
        async (evt): Promise<void> => {
            evt.preventDefault();
            evt.stopPropagation();
            const variant = {
                ...product,
                id: c_canonicalProductId || product.id,
            };

            if (widgetId && widgetType && metadataType) {
                widgetClicked({
                    location: EventLocation.cart,
                    action: EventAction['widget-add'],
                    data: {
                        widgetResponseId: uuidv4(),
                        widgetId,
                        widgetType,
                        metadataType,
                        itemId: product.id,
                        sku: variant.id,
                    },
                });
            }

            let source: GTMSourceEvent;
            if (location === RecommendationLocation.SAVE_FOR_LATER) {
                source = 'SAVE_FOR_LATER';
            } else if (location === RecommendationLocation.SEARCH_NO_RESULTS) {
                source = 'SEARCH';
            } else {
                source = 'RECOMMENDATIONS';
            }

            try {
                await addProductToBag.mutateAsync({
                    source,
                    product: variant,
                    quantity: 1,
                    shippingOptionType: ShippingOptionType.SHIP_TO,
                    applyExtend: false,
                });

                if (onAddProductToBag) {
                    onAddProductToBag(product);
                }
            } catch (error: unknown) {
                if (
                    error instanceof BFFHttpError &&
                    error.body?.uiErrorCode === UIErrorCode.MAXIMUM_AVAILABLE_QUANTITY
                ) {
                    setIsLimitReached(true);
                    toast(intl.formatMessage(errorMessage.quantityMaximumAvailable), 'error');
                }
            }
        },
        [
            onAddProductToBag,
            location,
            widgetClicked,
            addProductToBag,
            c_canonicalProductId,
            product,
            widgetId,
            widgetType,
            metadataType,
            toast,
            intl,
        ],
    );

    const iconButton = useMemo(
        () =>
            onAddProductToBag ? (
                <Tooltip
                    placement="top"
                    isDisabled={!isLimitReached}
                    variant="new-style"
                    label={
                        <Text textStyle="body-base-pepper-1000">
                            <FormattedMessage {...errorMessage.quantityMaximumAvailable} />
                        </Text>
                    }
                    shouldWrapChildren
                >
                    <IconButton
                        id="add-to-bag"
                        iconName="add-to-bag"
                        onClick={onAddToBag}
                        size="lg"
                        isDisabled={addProductToBag.isLoading || isLimitReached}
                        isLoading={addProductToBag.isLoading}
                    />
                </Tooltip>
            ) : null,
        [onAddToBag, addProductToBag.isLoading, onAddProductToBag, isLimitReached],
    );

    return (
        <Box
            display="flex"
            justifyContent="space-between"
            w="100%"
            flexDirection={isDrawer ? 'row' : { base: 'column', sm: 'row' }}
            className={
                isDrawer
                    ? 'sm:space-x-4'
                    : 'h-full min-h-[3.125rem] space-y-4 sm:space-x-4 sm:space-y-0'
            }
        >
            <Box display="flex" flexDirection="column" className="space-y-1">
                {((): ReactElement | null => {
                    if (suggestedPrice && price) {
                        return (
                            <>
                                <SalePrice
                                    id={`product-${product.id}-sale-price`}
                                    price={price}
                                    isPriceHeader={isPriceHeader}
                                    isDrawer={isDrawer}
                                />
                                <SuggestedPrice
                                    id={`product-${product.id}-suggested-price`}
                                    price={suggestedPrice}
                                    hideLabel={hideSuggLabel}
                                />
                            </>
                        );
                    }

                    if (regularPrice) {
                        if (price) {
                            return (
                                <>
                                    <SalePrice
                                        id={`product-${product.id}-sale-price`}
                                        price={price}
                                        isPriceHeader={isPriceHeader}
                                        isDrawer={isDrawer}
                                    />
                                    <RegularPrice
                                        id={`product-${product.id}-price`}
                                        price={regularPrice}
                                        isCrossed
                                    />
                                </>
                            );
                        }

                        if (suggestedPrice) {
                            return (
                                <>
                                    <RegularPrice
                                        id={`product-${product.id}-price`}
                                        price={regularPrice}
                                        isPriceHeader={isPriceHeader}
                                        isDrawer={isDrawer}
                                    />
                                    <SuggestedPrice
                                        id={`product-${product.id}-suggested-price`}
                                        price={suggestedPrice}
                                        hideLabel={hideSuggLabel}
                                    />
                                </>
                            );
                        }

                        return (
                            <RegularPrice
                                id={`product-${product.id}-price`}
                                price={regularPrice}
                                isPriceHeader={isPriceHeader}
                                isDrawer={isDrawer}
                            />
                        );
                    }

                    if (price) {
                        return (
                            <RegularPrice
                                id={`product-${product.id}-price`}
                                price={price}
                                isPriceHeader={isPriceHeader}
                                isDrawer={isDrawer}
                            />
                        );
                    }

                    return null;
                })()}
            </Box>
            {isDrawer
                ? iconButton
                : iconButton && (
                      <Flex justifyContent="flex-end" alignItems="flex-end">
                          {iconButton}
                      </Flex>
                  )}
        </Box>
    );
};
